import React, { useState, useEffect } from 'react';
import { Card, Heading, Text, Flex, Avatar, Dialog, Button } from '@radix-ui/themes';
import { CalendarIcon, CheckIcon, TargetIcon, StarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL, apiService, AttendanceRecord } from './services/api';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';
import './Attendance.css';

interface AttendanceProps {
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

const Attendance: React.FC<AttendanceProps> = ({ students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
    const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentDate, setCurrentDate] = useState(new Date());

    useEffect(() => {
        if (!selectedStudent) return;
        setLoading(true);
        setError(null);
        apiService.getAttendance(selectedStudent.memberId)
            .then(res => {
                if (res.success) {
                    setAttendance(res.attendance || []);
                } else {
                    setError('Failed to fetch attendance');
                }
            })
            .catch(() => setError('Failed to fetch attendance'))
            .finally(() => setLoading(false));
    }, [selectedStudent]);

    // Stats logic
    const now = new Date();
    const currentMonthNumber = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const currentMonthAttendance = attendance.filter(a => {
        try {
            const date = new Date(a.ATTENDENCE);
            return date.getMonth() + 1 === currentMonthNumber && date.getFullYear() === currentYear;
        } catch {
            return false;
        }
    }).length;

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const sixMonthAttendanceFiltered = attendance.filter(a => {
        try {
            const date = new Date(a.ATTENDENCE);
            return date >= sixMonthsAgo;
        } catch {
            return false;
        }
    }).length;

    const minMonthlyClasses = 10;
    const totalPossibleClasses = 6 * minMonthlyClasses;
    const sixMonthRate = totalPossibleClasses ? Math.round((sixMonthAttendanceFiltered / totalPossibleClasses) * 100) : 0;

    const totalPossible = attendance.length;

    // Get last 6 months attendance records for history
    const lastSixMonthsAttendance = attendance
        .filter(a => {
            try {
                const date = new Date(a.ATTENDENCE);
                return date >= sixMonthsAgo;
            } catch {
                return false;
            }
        })
        .sort((a, b) => {
            try {
                const dateA = new Date(a.ATTENDENCE);
                const dateB = new Date(b.ATTENDENCE);
                return dateB.getTime() - dateA.getTime(); // Sort by most recent first
            } catch {
                return 0;
            }
        });

    // Function to format date for display
    const formatAttendanceDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Function to get attendance status color
    const getAttendanceStatusColor = () => {
        return 'var(--green-9)'; // Green for attended classes
    };

    // Calendar functions
    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const isDateAttended = (date: Date | null) => {
        if (!date) return false;
        const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        return attendance.some(record => {
            try {
                const recordDate = new Date(record.ATTENDENCE);
                return recordDate.toISOString().split('T')[0] === dateString;
            } catch {
                return false;
            }
        });
    };

    const isToday = (date: Date | null) => {
        if (!date) return false;
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isCurrentMonth = (date: Date | null) => {
        if (!date) return false;
        return date.getMonth() === currentDate.getMonth() &&
            date.getFullYear() === currentDate.getFullYear();
    };

    const generateCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDayOfMonth = getFirstDayOfMonth(currentDate);
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            days.push(date);
        }

        return days;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            const today = new Date();
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

            if (direction === 'prev') {
                newDate.setMonth(newDate.getMonth() - 1);
                // Don't allow going before 6 months ago
                if (newDate < sixMonthsAgo) {
                    return prev;
                }
            } else {
                newDate.setMonth(newDate.getMonth() + 1);
                // Don't allow going beyond current month
                if (newDate > today) {
                    return prev;
                }
            }
            return newDate;
        });
    };

    const stats = [
        {
            icon: <CalendarIcon width="20" height="20" />,
            value: loading ? '...' : sixMonthAttendanceFiltered,
            label: '6-Month Attendance',
            color: 'blue',
        },
        {
            icon: <CheckIcon width="20" height="20" />,
            value: loading ? '...' : currentMonthAttendance,
            label: 'Current Month',
            color: 'green',
        },
        {
            icon: <TargetIcon width="20" height="20" />,
            value: loading ? '...' : `${sixMonthRate}%`,
            label: '6-Month Rate',
            color: 'orange',
        },
        {
            icon: <StarIcon width="20" height="20" />,
            value: loading ? '...' : totalPossible,
            label: 'Total Possible',
            color: 'purple',
        },
    ];

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />
            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={() => window.location.href = '/'}
                selectedStudent={selectedStudent}
            />
            <main className="home-content">
                <div className="content-container">
                    {/* Welcome Section */}
                    <Card className="welcome-card">
                        <Flex align="center" gap="4" justify="between">
                            <Flex align="center" gap="4">
                                <Avatar
                                    size="4"
                                    src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                    fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                                />
                                <Flex direction="column" gap="1">
                                    <Heading size="6">Welcome, {selectedStudent?.fullName || 'Student'}!</Heading>
                                    <Text size="2" color="gray">Here is your attendance record.</Text>
                                </Flex>
                            </Flex>
                            <button
                                onClick={() => setIsCalendarModalOpen(true)}
                                style={{
                                    backgroundColor: 'var(--blue-9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--blue-10)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--blue-9)';
                                }}
                            >
                                <CalendarIcon width="20" height="20" />
                            </button>
                        </Flex>
                    </Card>

                    {/* Stats Section */}
                    <div className="profile-stats-grid">
                        {stats.map((stat, idx) => (
                            <Card className="stat-card" key={idx}>
                                <Flex direction="column" gap="2" align="center">
                                    <div className="activity-icon" style={{ marginBottom: '8px' }}>{stat.icon}</div>
                                    <Text size="6" weight="bold" color={stat.color as any}>{stat.value}</Text>
                                    <Text size="2" color="gray">{stat.label}</Text>
                                </Flex>
                            </Card>
                        ))}
                    </div>

                    {/* Attendance History */}
                    <Card className="activity-card">
                        <Heading size="4" mb="4">Attendance History (Last 6 Months)</Heading>
                        {loading ? (
                            <Text size="3">Loading attendance history...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : lastSixMonthsAttendance.length > 0 ? (
                            <Flex direction="column" gap="3">
                                {lastSixMonthsAttendance.map((record, index) => (
                                    <Flex key={record.attendanceId} align="center" gap="3" className="activity-item">
                                        <div className="activity-icon" style={{ backgroundColor: getAttendanceStatusColor() }}>
                                            <CheckIcon width="16" height="16" />
                                        </div>
                                        <Flex direction="column" gap="1">
                                            <Text size="2" weight="bold">Class Attended</Text>
                                            <Text size="1" color="gray">
                                                {formatAttendanceDate(record.ATTENDENCE)}
                                            </Text>
                                        </Flex>
                                        <div className="activity-icon" style={{ backgroundColor: getAttendanceStatusColor(), marginLeft: 'auto' }}>
                                            <CheckIcon width="16" height="16" />
                                        </div>
                                    </Flex>
                                ))}
                            </Flex>
                        ) : (
                            <Text size="3">No attendance records available for the last 6 months</Text>
                        )}
                    </Card>



                    {error && (
                        <Card className="error">
                            <Text color="red">{error}</Text>
                        </Card>
                    )}
                </div>
            </main>

            {/* Calendar Modal */}
            <Dialog.Root open={isCalendarModalOpen} onOpenChange={setIsCalendarModalOpen}>
                <Dialog.Content style={{
                    maxWidth: 'min(95vw, 500px)',
                    maxHeight: 'min(95vh, 600px)',
                    width: '100%',
                    margin: '20px',
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000
                }}>
                    <Dialog.Title>
                        <Flex align="center" justify="between">
                            <Text size={{ initial: '3', sm: '4' }}>Attendance Calendar</Text>
                            <button
                                onClick={() => setIsCalendarModalOpen(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '28px',
                                    minHeight: '28px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <Text size="3" weight="bold">×</Text>
                            </button>
                        </Flex>
                    </Dialog.Title>
                    <Dialog.Description size={{ initial: '1', sm: '2' }} mb="3">
                        View your attendance calendar for {selectedStudent?.fullName || 'Student'}
                    </Dialog.Description>

                    <div style={{
                        maxHeight: 'calc(min(95vh, 600px) - 180px)',
                        overflowY: 'auto',
                        padding: '12px',
                        backgroundColor: 'var(--gray-1)',
                        borderRadius: '8px'
                    }}>
                        {/* Calendar Header */}
                        <Flex align="center" justify="between" mb="3">
                            {(() => {
                                const today = new Date();
                                const sixMonthsAgo = new Date();
                                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                                const isPrevDisabled = currentDate <= sixMonthsAgo;
                                const isNextDisabled = currentDate >= today;

                                return (
                                    <>
                                        <button
                                            onClick={() => navigateMonth('prev')}
                                            disabled={isPrevDisabled}
                                            style={{
                                                backgroundColor: isPrevDisabled ? 'var(--gray-2)' : 'var(--gray-3)',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '6px',
                                                cursor: isPrevDisabled ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: isPrevDisabled ? 0.5 : 1,
                                                minWidth: '32px',
                                                minHeight: '32px'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isPrevDisabled) {
                                                    e.currentTarget.style.backgroundColor = 'var(--gray-4)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isPrevDisabled) {
                                                    e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                                }
                                            }}
                                        >
                                            <ChevronLeftIcon width="14" height="14" />
                                        </button>

                                        <Text size={{ initial: '2', sm: '3' }} weight="bold" style={{ textAlign: 'center' }}>
                                            {currentDate.toLocaleDateString('en-US', {
                                                month: 'long',
                                                year: 'numeric'
                                            })}
                                        </Text>

                                        <button
                                            onClick={() => navigateMonth('next')}
                                            disabled={isNextDisabled}
                                            style={{
                                                backgroundColor: isNextDisabled ? 'var(--gray-2)' : 'var(--gray-3)',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '6px',
                                                cursor: isNextDisabled ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: isNextDisabled ? 0.5 : 1,
                                                minWidth: '32px',
                                                minHeight: '32px'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isNextDisabled) {
                                                    e.currentTarget.style.backgroundColor = 'var(--gray-4)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isNextDisabled) {
                                                    e.currentTarget.style.backgroundColor = 'var(--gray-3)';
                                                }
                                            }}
                                        >
                                            <ChevronRightIcon width="14" height="14" />
                                        </button>
                                    </>
                                );
                            })()}
                        </Flex>

                        {/* Calendar Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '2px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '8px',
                            border: '1px solid var(--gray-4)'
                        }}>
                            {/* Day Headers */}
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                <div key={day} style={{
                                    textAlign: 'center',
                                    padding: '4px',
                                    fontWeight: 'bold',
                                    fontSize: '10px',
                                    color: 'var(--gray-11)',
                                    backgroundColor: 'var(--gray-2)',
                                    borderRadius: '4px'
                                }}>
                                    {day}
                                </div>
                            ))}

                            {/* Calendar Days */}
                            {generateCalendarDays().map((date, index) => (
                                <div
                                    key={index}
                                    style={{
                                        aspectRatio: '1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid var(--gray-4)',
                                        borderRadius: '4px',
                                        backgroundColor: date ? 'white' : 'transparent',
                                        position: 'relative',
                                        cursor: date ? 'pointer' : 'default',
                                        fontSize: '11px',
                                        fontWeight: isToday(date) ? 'bold' : 'normal',
                                        color: date && isCurrentMonth(date) ? 'var(--gray-12)' : 'var(--gray-8)',
                                        ...(isToday(date) && {
                                            border: '2px solid var(--blue-9)',
                                            backgroundColor: 'var(--blue-9)',
                                            color: 'white'
                                        }),
                                        ...(date && isDateAttended(date) && {
                                            backgroundColor: 'var(--green-9)',
                                            border: '1px solid var(--green-9)',
                                            color: 'white'
                                        })
                                    }}
                                    title={date ? `${date.toLocaleDateString()}${isDateAttended(date) ? ' - Attended' : ''}` : ''}
                                >
                                    {date ? date.getDate() : ''}
                                    {date && isDateAttended(date) && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '1px',
                                            right: '1px',
                                            width: '5px',
                                            height: '5px',
                                            backgroundColor: 'white',
                                            borderRadius: '50%'
                                        }} />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Legend */}
                        <Flex gap="2" mt="2" justify="center" wrap="wrap">
                            <Flex align="center" gap="1">
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: 'var(--green-9)',
                                    border: '1px solid var(--green-9)',
                                    borderRadius: '2px'
                                }} />
                                <Text size="1">Attended</Text>
                            </Flex>
                            <Flex align="center" gap="1">
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: 'var(--blue-9)',
                                    border: '2px solid var(--blue-9)',
                                    borderRadius: '2px'
                                }} />
                                <Text size="1">Today</Text>
                            </Flex>
                            <Flex align="center" gap="1">
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: 'white',
                                    border: '1px solid var(--gray-4)',
                                    borderRadius: '2px'
                                }} />
                                <Text size="1">Regular Day</Text>
                            </Flex>
                        </Flex>
                    </div>

                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Close
                            </Button>
                        </Dialog.Close>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default Attendance; 